import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Heading, VStack, Input, Select, Button, Text, Flex, Progress, useToast,
    SimpleGrid, Icon, Alert, AlertIcon, AlertTitle, AlertDescription, FormControl, FormLabel, FormErrorMessage,
    Checkbox, CheckboxGroup, Wrap, WrapItem
} from '@chakra-ui/react';
import { FaFileArchive, FaCheckCircle, FaExclamationTriangle, FaDownload } from 'react-icons/fa';
import { startXmlNfeExport, getEmitentesXmlNfe, getExportStatus, downloadExportFile } from "../services/api";
import { TIPOS_NOTA, dataLocal, formatarCnpj, validarPeriodo } from "../utils/notasFiscais";

const mesAnterior = () => {
    const hoje = new Date();
    return {
        data_inicio: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)),
        data_fim: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 0)),
    };
};

const primeiroErro = (error) => {
    const errors = error?.response?.data?.errors;
    if (errors) {
        const primeiro = Object.values(errors).flat()[0];
        if (primeiro) return primeiro;
    }
    if (error?.response?.status === 403) return 'Exportação restrita a administradores.';
    return error?.response?.data?.message || 'Ocorreu um erro inesperado.';
};

const ExportarXmlNfe = () => {
    const [filters, setFilters] = useState(() => ({ ...mesAnterior(), cnpj: '', tipos: ['venda'] }));
    const [emitentes, setEmitentes] = useState([]);
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const toast = useToast();
    const intervalRef = useRef(null);

    const erroPeriodo = validarPeriodo(filters);
    const erroTipos = filters.tipos.length === 0 ? 'Marque pelo menos um tipo de nota.' : null;

    useEffect(() => {
        let ativo = true;
        getEmitentesXmlNfe()
            .then(lista => { if (ativo) setEmitentes(Array.isArray(lista) ? lista : []); })
            .catch(() => { if (ativo) setEmitentes([]); });
        return () => { ativo = false; };
    }, []);

    const pararPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleStartExport = async () => {
        if (erroPeriodo || erroTipos) {
            toast({ title: 'Filtros inválidos', description: erroPeriodo || erroTipos, status: 'warning', duration: 5000, isClosable: true });
            return;
        }

        pararPolling();
        setIsGenerating(true);
        setJobStatus(null);
        setJobId(null);

        try {
            const data = await startXmlNfeExport({
                data_inicio: filters.data_inicio,
                data_fim: filters.data_fim,
                cnpj: filters.cnpj || null,
                tipos: filters.tipos,
            });
            setJobId(data.job_id);
            setJobStatus({ status: 'pending', progress: 0, message: 'Na fila de exportação...' });
        } catch (error) {
            toast({ title: 'Erro ao iniciar a exportação', description: primeiroErro(error), status: 'error', duration: 9000, isClosable: true });
            setIsGenerating(false);
        }
    };

    const fetchStatus = useCallback(async () => {
        if (!jobId) return;

        try {
            const statusData = await getExportStatus(jobId);
            setJobStatus(statusData);

            if (['completed', 'failed'].includes(statusData.status)) {
                setIsGenerating(false);
                pararPolling();
            }
        } catch (error) {
            console.error("Erro ao buscar status:", error);
            setJobStatus({ status: 'failed', message: 'Não foi possível acompanhar a exportação. Tente gerar de novo.' });
            setIsGenerating(false);
            pararPolling();
        }
    }, [jobId, pararPolling]);

    useEffect(() => {
        if (isGenerating && jobId && !intervalRef.current) {
            intervalRef.current = setInterval(fetchStatus, 3000);
        }
        return pararPolling;
    }, [isGenerating, jobId, fetchStatus, pararPolling]);

    const handleDownload = async () => {
        if (!jobStatus?.file_name) return;
        setIsDownloading(true);
        try {
            await downloadExportFile(jobStatus.file_name);
            toast({ title: 'Download iniciado', status: 'success', duration: 3000, isClosable: true });
        } catch (error) {
            toast({ title: 'Erro no download', description: 'Não foi possível baixar o arquivo. Gere a exportação de novo.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            setIsDownloading(false);
            setJobStatus(null);
            setJobId(null);
        }
    };

    const renderStatus = () => {
        if (!jobStatus) return null;
        switch (jobStatus.status) {
            case 'pending':
            case 'running':
                return (
                    <Box w="100%">
                        <Flex justify="space-between" mb={1} gap={2}>
                            <Text fontSize="sm" fontWeight="medium">{jobStatus.message}</Text>
                            <Text fontSize="sm" fontWeight="bold" color="orange.500">{jobStatus.progress || 0}%</Text>
                        </Flex>
                        <Progress value={jobStatus.progress || 0} size="md" colorScheme="orange" borderRadius="md" isIndeterminate={jobStatus.status === 'pending'} />
                    </Box>
                );
            case 'completed':
                return (
                    <Alert status="success" borderRadius="md" variant="left-accent" alignItems="flex-start">
                        <AlertIcon as={FaCheckCircle} />
                        <Box flex="1">
                            <AlertTitle>Arquivo pronto!</AlertTitle>
                            <AlertDescription display="block" fontSize="sm" mb={3}>{jobStatus.message}</AlertDescription>
                            <Button leftIcon={<FaDownload />} colorScheme="green" onClick={handleDownload} isLoading={isDownloading} loadingText="Baixando..." size="sm" w={{ base: 'full', sm: 'auto' }}>
                                Baixar ZIP
                            </Button>
                        </Box>
                    </Alert>
                );
            case 'failed':
                return (
                    <Alert status="error" borderRadius="md" variant="left-accent">
                        <AlertIcon as={FaExclamationTriangle} />
                        <Box flex="1">
                            <AlertTitle>Falha na exportação</AlertTitle>
                            <AlertDescription fontSize="sm">{jobStatus.message || 'Ocorreu um erro inesperado.'}</AlertDescription>
                        </Box>
                    </Alert>
                );
            default: return null;
        }
    };

    return (
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
            <Box p={{ base: 4, md: 6 }} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                <Heading size="md" mb={4}>1. Filtros</Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl isInvalid={!!erroPeriodo}>
                        <FormLabel fontSize="sm" mb={1}>Emissão de</FormLabel>
                        <Input type="date" name="data_inicio" value={filters.data_inicio} onChange={handleFilterChange} max={filters.data_fim || undefined} />
                    </FormControl>
                    <FormControl isInvalid={!!erroPeriodo}>
                        <FormLabel fontSize="sm" mb={1}>Emissão até</FormLabel>
                        <Input type="date" name="data_fim" value={filters.data_fim} onChange={handleFilterChange} min={filters.data_inicio || undefined} />
                        {erroPeriodo && <FormErrorMessage>{erroPeriodo}</FormErrorMessage>}
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="sm" mb={1}>Emitente</FormLabel>
                        <Select name="cnpj" value={filters.cnpj} onChange={handleFilterChange}>
                            <option value="">Todos os emitentes</option>
                            {emitentes.map(e => (
                                <option key={e.cnpj} value={e.cnpj}>{formatarCnpj(e.cnpj)}{e.nome ? ` · ${e.nome}` : ''}</option>
                            ))}
                        </Select>
                    </FormControl>
                </SimpleGrid>
                <FormControl isInvalid={!!erroTipos} mt={4}>
                    <FormLabel fontSize="sm" mb={2}>Tipo de nota</FormLabel>
                    <CheckboxGroup colorScheme="orange" value={filters.tipos} onChange={(tipos) => setFilters(prev => ({ ...prev, tipos }))}>
                        <Wrap spacing={{ base: 3, md: 5 }}>
                            {TIPOS_NOTA.map(t => (
                                <WrapItem key={t.value}>
                                    <Checkbox value={t.value}>{t.label}</Checkbox>
                                </WrapItem>
                            ))}
                        </Wrap>
                    </CheckboxGroup>
                    {erroTipos && <FormErrorMessage>{erroTipos}</FormErrorMessage>}
                </FormControl>
            </Box>

            <Box p={{ base: 4, md: 6 }} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                <Heading size="md" mb={4}>2. Gere o arquivo</Heading>
                <VStack spacing={4}>
                    <Button
                        leftIcon={<Icon as={FaFileArchive} />}
                        colorScheme="orange"
                        onClick={handleStartExport}
                        isLoading={isGenerating}
                        loadingText="Gerando..."
                        isDisabled={isGenerating || !!erroPeriodo || !!erroTipos}
                        w="full"
                        size="lg"
                    >
                        Gerar ZIP
                    </Button>
                    {renderStatus()}
                </VStack>
            </Box>
        </VStack>
    );
};

export default ExportarXmlNfe;
